import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SlideOutWrapper } from './SlideOutWrapper';
import InterstateTradeFormCard from '../tradeForm/TradeFormCard.interstate';
import { UnsavedChangesConfirmationModal, useGetDrawerWidth, useUnsavedChangesModal } from '@makemydeal/dr-dash-components';
import { AddTradeInButton } from '../tradeForm/MultiTradeIn/AddTradeInButton';
import { Box } from '@interstate/components/Box';
import { Tabs } from '@interstate/components/Tabs';
import type { FooterActionsType } from '@interstate/components/FooterActions';
import { PrinterIcon } from '@interstate/components/Icons/PrinterIcon';
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import { getReturnToOfferButton } from '@makemydeal/dr-shared-ui-utils';
import * as tradeReconCostSelectors from '@makemydeal/dr-dash-store/selectors/tradeReconCostSelectors';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as priorBalanceSelectors from '@makemydeal/dr-dash-store/selectors/priorBalanceSelectors';
import * as tradeReconCostActionCreators from '@makemydeal/dr-dash-store/actions/tradeReconCostActionCreators';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as printPdfActionCreator from '@makemydeal/dr-dash-store/actions/printPdfActionCreator';
import * as printPdfSelectors from '@makemydeal/dr-dash-store/selectors/printPdfSelectors';

import { TradeReconCostCard } from '../tradeReconCost';
import { PriorBalanceCard } from '../priorBalance/PriorBalanceCard';
import { usePriorBalanceForm } from '../priorBalance/usePriorBalanceForm';
import { PRIOR_BALANCE_TAB_LABEL, PRIOR_BALANCE_TAB_VALUE } from '../priorBalance/constants';
import type { SlideOutComponentProps } from '../../types';

export const TradeInSlideOut = ({ isOpen, onClose }: SlideOutComponentProps) => {
    const dispatch = useDispatch();
    const panelWidth = useGetDrawerWidth({
        large: 1200,
        extraLarge: 1200
    });

    const tradeIns = useSelector(tradeInsSelectors.getAllTradeIns);
    const tradeReconData = useSelector(tradeReconCostSelectors.getTradeReconData);
    const isTradeReconDataValid = useSelector(tradeReconCostSelectors.getIsTradeReconDataValid);
    const isUpdatingTradeReconData = useSelector(tradeReconCostSelectors.getIsUpdatingTradeReconData);
    const isTradeReconUpdateSuccess = useSelector(tradeReconCostSelectors.getIsTradeReconUpdateSuccess);
    const initialTab = useSelector(baseDealSelectors.getTradeInSlideoutTab);
    const isPrintLoading = useSelector(printPdfSelectors.isLoading);
    const isPriorBalanceApplicable = useSelector(priorBalanceSelectors.isPriorBalanceApplicable);
    const priorBalanceForm = usePriorBalanceForm(isOpen);

    // Tab state
    const [activeTab, setActiveTab] = useState<string>(initialTab);

    useEffect(() => {
        if (isOpen) {
            setActiveTab(initialTab);
        }
    }, [isOpen, initialTab]);

    const {
        isDirtyRef: isTradeInFormDirtyRef,
        showConfirmationModal,
        handleClose,
        handleCancelChanges,
        handleContinueEditing
    } = useUnsavedChangesModal({
        savedData: tradeIns,
        onClose
    });

    const handleTabChange = useCallback((value: string) => {
        setActiveTab(value);
    }, []);

    useEffect(() => {
        if (isTradeReconUpdateSuccess) handleClose();
    }, [isTradeReconUpdateSuccess]);

    const availableTabs = [
        {
            component: (
                <div data-testid="trade-in-form-tab">
                    <InterstateTradeFormCard isEditDisabled={false} isTradeInFormDirtyRef={isTradeInFormDirtyRef} />
                </div>
            ),
            value: 'trade-in',
            label: 'Edit Trade Info'
        },
        {
            component: (
                <div data-testid="trade-recon-cost-tab">
                    <TradeReconCostCard />
                </div>
            ),
            value: 'recon-cost',
            label: 'Trade Reconditioning'
        },
        ...(isPriorBalanceApplicable
            ? [
                  {
                      component: (
                          <div data-testid="prior-balance-tab">
                              <PriorBalanceCard form={priorBalanceForm} />
                          </div>
                      ),
                      value: PRIOR_BALANCE_TAB_VALUE,
                      label: PRIOR_BALANCE_TAB_LABEL
                  }
              ]
            : [])
    ];

    const effectiveTab = availableTabs.some((tab) => tab.value === activeTab) ? activeTab : availableTabs[0].value;
    const tabs = availableTabs.map((tab) => ({ ...tab, active: tab.value === effectiveTab }));

    // Footer configuration for recon cost view
    const tradeReconCostFooterProps: FooterActionsType = {
        layout: 'rightAligned',
        primary: {
            label: 'Apply',
            'data-testid': 'apply-trade-recon-settings',
            disabled: !isTradeReconDataValid,
            isLoading: isUpdatingTradeReconData,
            action: () => {
                dispatch(tradeReconCostActionCreators.saveTradeReconCost(tradeReconData));
                dispatch(offerActionCreators.saveOffer({ source: 'tradeReconSettingsApply' }));
            }
        },
        options: [
            {
                label: 'Cancel',
                'data-testid': 'cancel-trade-recon-cost',
                buttonStyle: 'tertiary' as const,
                action: handleClose
            },
            {
                label: (
                    <>
                        Apply and Print <PrinterIcon />
                    </>
                ) as unknown as string,
                'data-testid': 'apply-and-print-trade-recon-cost',
                buttonStyle: 'secondary' as const,
                disabled: !isTradeReconDataValid,
                isLoading: isPrintLoading,
                action: () => {
                    dispatch(tradeReconCostActionCreators.saveAndPrintTradeReconCost(tradeReconData));
                    dispatch(printPdfActionCreator.generatePdf('tradeEvaluation'));
                }
            }
        ]
    };

    const priorBalanceFooterProps: FooterActionsType = {
        layout: 'rightAligned',
        primary: {
            label: 'Apply',
            'data-testid': 'apply-prior-balance',
            disabled: !priorBalanceForm.isValid || !priorBalanceForm.isDirty,
            action: () => {
                priorBalanceForm.apply();
                handleClose();
            }
        },
        options: [
            {
                label: 'Cancel',
                'data-testid': 'cancel-prior-balance',
                buttonStyle: 'tertiary' as const,
                action: handleClose
            }
        ]
    };

    const getFooter = () => {
        if (effectiveTab === 'recon-cost') return tradeReconCostFooterProps;
        if (effectiveTab === PRIOR_BALANCE_TAB_VALUE) return priorBalanceFooterProps;
        return getReturnToOfferButton(handleClose);
    };

    return (
        <>
            <SlideOutWrapper
                title={
                    <Box sx={{ gap: '8px', display: 'flex', alignItems: 'center' }}>
                        Trades
                        {effectiveTab === 'trade-in' ? <AddTradeInButton /> : null}
                    </Box>
                }
                isOpen={isOpen}
                onClose={handleClose}
                panelWidth={panelWidth}
                testId="tab-slideout-trade-in"
                footer={getFooter()}
            >
                <Tabs tabs={tabs} onActivate={handleTabChange} data-testid="trade-in-tabs" id="trade-in-tabs" />
            </SlideOutWrapper>
            <UnsavedChangesConfirmationModal
                show={showConfirmationModal}
                onCancelChanges={handleCancelChanges}
                onContinueEditing={handleContinueEditing}
            />
        </>
    );
};
